import { motion } from 'motion/react';
import { glowColorFor } from './RevealBackground';

interface RevealBackgroundTintProps {
  /** Badge name from `mark_as_opened` — sets the tint colour via
   *  `glowColorFor()`. While null we don't render anything. */
  badge: string;
}

/**
 * Badge-colour wash that fades in over the dark page background
 * the moment `mark_as_opened` returns. Two-layer paint:
 *
 *   1. Radial gradient — saturated bullseye behind the focal pack
 *      (alpha ≈ 80 % at the centre, soft drop to transparent at the
 *      edges). Reinforces where the eye is already looking.
 *   2. Linear gradient — full-bleed top-to-bottom wash in the same
 *      badge colour (alpha ≈ 25 % top → 6 % bottom). This is what
 *      makes the whole room feel "turned purple" / "turned amber"
 *      instead of just a halo behind the card.
 *
 * Both layers fade in together over ~1.2 s with a soft easeOut so
 * the recolour reads as a swell, not a snap. Sits in PackReveal
 * between the page background and the rays so the rays read on
 * top of the tinted area.
 */
export function RevealBackgroundTint({ badge }: RevealBackgroundTintProps) {
  // `glowColorFor` returns a `var(--ui-badge-…, fallback)` expression,
  // so we layer alpha via `color-mix(in srgb, X Y%, transparent)`
  // instead of the old hex+alpha-suffix trick (`${tint}cc`), which
  // doesn't work once the value is a CSS-var.
  const tint = glowColorFor(badge);
  const at = (pct: number) =>
    `color-mix(in srgb, ${tint} ${pct}%, transparent)`;
  return (
    <motion.div
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage: `
          radial-gradient(120% 90% at 50% 38%, ${at(80)} 0%, ${at(40)} 38%, transparent 78%),
          linear-gradient(180deg, ${at(25)} 0%, ${at(10)} 60%, transparent 100%)
        `,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
    />
  );
}
